import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminSummary } from '@/api/auth';

interface AuthState {
  token: string | null;
  admin: AdminSummary | null;
  setSession: (token: string, admin: AdminSummary) => void;
  clear: () => void;
}

/**
 * Auth state lives in localStorage so a page reload keeps the session.
 * Magic-link tokens grant a 7-day server-side JWT (kind: 'admin') —
 * that's our session length. If a 401 ever lands, the client interceptor
 * calls clear() and the router redirects to /login.
 *
 * Separate localStorage key from the inference/fleet consoles so the
 * three surfaces can coexist on the same machine without overwriting
 * each other's sessions.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      token: null,
      admin: null,
      setSession: (token, admin) => set({ token, admin }),
      clear: () => set({ token: null, admin: null })
    }),
    { name: 'nurev-admin-auth' }
  )
);

export const isAuthenticated = (): boolean => !!useAuthStore.getState().token;
